import { programsRequireCreditBundle } from "@outschool/funding-programs-shared";
import {
  UpdateCurrentUserDetailsMutation,
  UpdateCurrentUserDetailsMutationVariables,
} from "@outschool/gql-frontend-generated";
import { gql, useMutation } from "@outschool/ui-apollo";
import {
  FullCurrentUserFragment,
  useEsaExperience,
  useFundingProgramAbbr,
  useSession,
} from "@outschool/ui-auth";
import React from "react";

const updateCurrentUserDetailsMutation = gql`
  mutation UpdateCurrentUserDetails($details: UserDetailsInput) {
    updateCurrentUser(details: $details) {
      ...FullCurrentUserFragment
    }
  }
  ${FullCurrentUserFragment}
`;

export function useMembershipRequirement() {
  const { isLoggedIn, currentUser } = useSession();
  const { isEsaExperience } = useEsaExperience();
  const esaState = useFundingProgramAbbr();

  const [updateCurrentUserDetails] = useMutation<
    UpdateCurrentUserDetailsMutation,
    UpdateCurrentUserDetailsMutationVariables
  >(updateCurrentUserDetailsMutation);

  React.useEffect(() => {
    if (currentUser?.requiresMembershipForEnrollment === true) {
      updateCurrentUserDetails({
        variables: {
          details: {
            requiresMembershipForEnrollment: false,
          },
        },
      });
    }
  }, [currentUser?.requiresMembershipForEnrollment, updateCurrentUserDetails]);
  // membership is currently optional for all users
  const requiresMembershipForEnrollment = false;

  const requiresEsaMembershipForEnrollment =
    isLoggedIn &&
    isEsaExperience &&
    programsRequireCreditBundle.includes(esaState ?? "");

  const mustPurchaseMembership = false;

  const mustPurchaseEsaMembership =
    requiresEsaMembershipForEnrollment && !currentUser?.hasEsaMembership;

  const showCredits =
    requiresMembershipForEnrollment ||
    requiresEsaMembershipForEnrollment ||
    currentUser?.canPayWithSubscriptionCredits;

  return {
    // Logged in or logged out user is required to have a membership to enroll, regardless if they have a membership or not
    requiresMembershipForEnrollment,
    // User `requiresMembershipForEnrollment` + does NOT have a membership
    mustPurchaseMembership,

    // Logged in user is required to have an ESA membership to enroll, regardless if they have an ESA membership or not
    requiresEsaMembershipForEnrollment,
    // User `requiresEsaMembershipForEnrollment` + does NOT have a membership
    mustPurchaseEsaMembership,

    // User should see credits vs dollar amounts
    showCredits,
  };
}
